
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { ConfigGlobalTypeSingle } from "@shared/lib/types";
import { SelectButton } from "@shared/ui/Select";
import { useMemo } from "react";
import styles from "./InputNumberFilter.module.scss";

interface InputNumberFilterProps {
  filter: ConfigGlobalTypeSingle;
  showTitle?: boolean;
  selectedFilters?: any;
  setSelectedFilter?: (updateFn: (value: any) => any) => void;
}

export function InputNumberFilter({
  filter,
  showTitle = true,
  selectedFilters: selectedFiltersProp,
  setSelectedFilter: setSelectedFilterProp,
}: InputNumberFilterProps) {
  const globalStore = useGetGlobalDiscoveryStore();
  const selectedFilters = selectedFiltersProp ?? globalStore.selectedFilters;
  const setSelectedFilter = setSelectedFilterProp ?? globalStore.setSelectedFilter;

  // Build options from optionsList
  const options = useMemo(() => {
    if (!filter.optionsList || filter.optionsList.length === 0) return [];

    return filter.optionsList.map((item) => ({
      label: item.label,
      value:
        typeof item.value === "number"
          ? String(item.value)
          : typeof item.value === "string"
            ? item.value
            : String(item.value),
      numericValue:
        typeof item.value === "number" ? item.value : Number(item.value),
    }));
  }, [filter.optionsList]);

  // Get current selected value (SelectButton uses string values)
  const currentValue = useMemo(() => {
    const value = selectedFilters[filter.facetName];
    if (typeof value === "number") {
      return String(value);
    }
    if (typeof value === "string") {
      return value;
    }
    return undefined;
  }, [selectedFilters, filter.facetName]);

  const handleChange = (value: string | string[] | undefined, option: any) => {
    // SelectButton onChange receives value directly
    if (!value) {
      setSelectedFilter((prevFilters) => {
        return {
          ...prevFilters,
          [filter.facetName]: undefined,
          filterTags: {
            ...(prevFilters.filterTags || {}),
            [filter.facetName]: undefined,
          },
        };
      });
      return;
    }

    // Handle single select (not array)
    const selectedValue = Array.isArray(value) ? value[0] : value;
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const label = selectedOption?.label || selectedValue;

    // Convert string value to number if needed
    const numericValue =
      selectedOption?.numericValue !== undefined
        ? selectedOption.numericValue
        : Number(selectedValue);

    setSelectedFilter((prevFilters) => {
      return {
        ...prevFilters,
        [filter.facetName]: isNaN(numericValue) ? selectedValue : numericValue,
        filterTags: {
          ...(prevFilters.filterTags || {}),
          [filter.facetName]: selectedValue ? label : undefined,
        },
      };
    });
  };

  return (
    <div className={styles.inputNumberFilter}>
      {showTitle && <label className={styles.label}>{filter.title}</label>}
      <SelectButton
        size="middle"
        className={styles.select}
        placeholder={filter.placeHolder || `Select ${filter.title}`}
        options={options}
        value={currentValue}
        onChange={handleChange}
        searchable={false}
        allowClear
      />
    </div>
  );
}
